import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Spin } from "antd";
import styled from "styled-components";

interface ISnapshotPreviewProps {
  snapshotUrl?: string;
  loading?: boolean;
}

const SnapshotPreview: FC<ISnapshotPreviewProps> = ({ snapshotUrl, loading }) => {
  const { t } = useTranslation('federatedViewer');

  if (loading) {
    return (
      <LoadingContainer>
        <Spin size="large" />
        <LoadingText>{t('snapshot.capturing')}</LoadingText>
      </LoadingContainer>
    );
  }

  if (!snapshotUrl) {
    return (
      <EmptyContainer>
        <EmptyText>{t('snapshot.empty')}</EmptyText>
      </EmptyContainer>
    );
  }

  return (
    <Container>
      <Title>{t('snapshot.title')}</Title>
      <Description>
        {t('snapshot.description')}
      </Description>
      <ImageWrapper>
        <SnapshotImage src={snapshotUrl} alt={t('viewpointAlt')} />
      </ImageWrapper>
      <InfoText>
        {t('snapshot.info')}
      </InfoText>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 16px;
`;

const EmptyContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 24px;
`;

const Title = styled.h3`
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 600;
  color: #324150;
`;

const Description = styled.p`
  margin: 0 0 16px 0;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
`;

const ImageWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 12px;
`;

const SnapshotImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 4px;
`;

const InfoText = styled.p`
  margin: 0;
  font-size: 11px;
  color: #9ca3af;
  text-align: center;
`;

const LoadingText = styled.p`
  margin: 0;
  font-size: 14px;
  color: #6b7280;
`;

const EmptyText = styled.p`
  margin: 0;
  font-size: 14px;
  color: #9ca3af;
  text-align: center;
`;

export default SnapshotPreview;
